import { useEffect, useState } from "react";
import { authAxios } from "../../utils/auth";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { ArrowRightLeft } from "lucide-react";

const GOLD = "#C5A55A";
const TIER_COLORS = {
  watch: "bg-white/10 text-white/70",
  prewarn: "bg-amber-500/20 text-amber-300",
  freeze: "bg-red-500/20 text-red-400",
  high_value: "bg-cyan-500/20 text-cyan-300",
  cleared: "bg-emerald-500/15 text-emerald-400",
  confirmed_fraud: "bg-red-900/40 text-red-300",
};

/**
 * CaseDetailPanel — inline (non-modal) rendering of a single fraud case.
 *
 * Extracted from the legacy CaseDetailModal so the new /admin/fraud/defender
 * page can render the case body next to the case list without an overlay.
 *
 * Self-loads by case_id and re-fetches on caseId change. Emits `onUpdated`
 * after any admin action so the parent can refresh its case list.
 */
export default function CaseDetailPanel({ caseId, onUpdated }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [cascade, setCascade] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!caseId) return;
    setLoading(true);
    try {
      const r = await authAxios.get(`/admin/fraud/cases/${caseId}`);
      setData(r.data);
    } catch {
      toast.error("Failed to load case");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); setNote(""); /* eslint-disable-next-line */ }, [caseId]);

  const c = data?.case;
  const affiliate = data?.affiliate || {};
  const commissions = data?.commissions || [];

  const doAction = async (action) => {
    setBusy(true);
    try {
      await authAxios.post(`/admin/fraud/cases/${c.case_id}/action`, { action, note, cascade });
      toast.success(action === "add_note" ? "Note saved" : `Case ${action.replace(/_/g, " ")}`);
      if (action === "add_note") setNote("");
      await load();
      if (onUpdated) onUpdated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  const doVouch = async () => {
    setBusy(true);
    try {
      await authAxios.post(`/admin/fraud/users/${c.user_id}/vouch`, { vouched: true, reason: note });
      toast.success("Affiliate vouched (12mo)");
      await load();
      if (onUpdated) onUpdated();
    } catch { toast.error("Vouch failed"); } finally { setBusy(false); }
  };

  const approveHighValue = async () => {
    setBusy(true);
    try {
      const r = await authAxios.post(`/admin/fraud/cases/${c.case_id}/approve-high-value`);
      toast.success(`Released ${r.data.released} row(s) for payout`);
      await load();
      if (onUpdated) onUpdated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Approve failed");
    } finally { setBusy(false); }
  };

  const splitHighValue = async () => {
    if (!window.confirm("Split this high-value commission into sub-$500 chunks?")) return;
    setBusy(true);
    try {
      const r = await authAxios.post(`/admin/fraud/cases/${c.case_id}/split-high-value`, { chunk_cents: 49900 });
      toast.success(`Split into ${r.data.rows_split} row(s)`);
      await load();
      if (onUpdated) onUpdated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Split failed");
    } finally { setBusy(false); }
  };

  if (!caseId) {
    return <div className="p-8 text-center text-white/40 text-sm" data-testid="case-panel-empty">Select a case from the list.</div>;
  }
  if (loading || !c) {
    return <div className="p-8 text-center text-white/40 text-sm" data-testid="case-panel-loading">Loading case…</div>;
  }

  return (
    <div className="space-y-4" data-testid="case-detail-panel">
      {/* Header row */}
      <div className="flex items-center justify-between border-b border-white/5 pb-3">
        <div>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-0.5 rounded text-[10px] uppercase ${TIER_COLORS[c.status] || TIER_COLORS.watch}`}>{c.status}</span>
            <span className="text-white/90 font-mono text-xs">{c.case_id}</span>
            {c.threat_class && <span className="px-2 py-0.5 rounded text-[10px] bg-white/5 text-white/60">Threat {c.threat_class}</span>}
          </div>
          <div className="text-white/50 text-xs mt-1">
            Risk <span style={{ color: GOLD }}>{c.risk_score || 0}/100</span>
            &nbsp;·&nbsp; Trust <span className="text-white/80">{c.trust_score || 0}/100</span>
            {c.trust_bias_flag && <span className="ml-2 text-sky-300 text-[10px]">TRUSTED — admin may downgrade</span>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_240px] gap-4">
        <div className="space-y-4 min-w-0">
          <Card className="bg-[#0f0f0f] border-white/5 p-4" data-testid="affiliate-card">
            <div className="flex items-center justify-between mb-2">
              <div className="text-white/40 text-[10px] uppercase">Affiliate</div>
              <button
                onClick={() => { window.location.href = `/plog/commission_ledger_v2?user_id=${encodeURIComponent(c.user_id)}`; }}
                className="text-[10px] px-2 py-1 rounded bg-[#C5A55A]/10 hover:bg-[#C5A55A]/20 border border-[#C5A55A]/30 text-[#C5A55A] flex items-center gap-1"
                data-testid="view-commissions-btn"
              >
                <ArrowRightLeft className="w-3 h-3" /> View commissions
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div><span className="text-white/40">Name:</span> <span className="text-white">{affiliate.name || "—"}</span></div>
              <div><span className="text-white/40">Email:</span> <span className="text-white">{affiliate.email || "—"}</span></div>
              <div><span className="text-white/40">Aff ID:</span> <span className="text-white font-mono">{affiliate.aff_id || "—"}</span></div>
              <div><span className="text-white/40">Tier:</span> <span className="text-white">{affiliate.affiliate_compensation_profile || "STANDARD"}</span></div>
              <div><span className="text-white/40">Joined:</span> <span className="text-white">{affiliate.created_at ? new Date(affiliate.created_at).toLocaleDateString() : "—"}</span></div>
              <div><span className="text-white/40">Vouched:</span> <span className="text-white">{affiliate.admin_vouched_at ? new Date(affiliate.admin_vouched_at).toLocaleDateString() : "—"}</span></div>
            </div>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Card className="bg-[#0f0f0f] border-red-500/20 p-4" data-testid="risk-signals-panel">
              <div className="text-red-400 text-[10px] uppercase mb-2">Risk Signals</div>
              <div className="space-y-2">
                {(c.flags || []).map((f, i) => (
                  <div key={i} className="text-xs border-l-2 border-red-500/50 pl-2">
                    <div className="flex items-center justify-between">
                      <span className="text-white font-mono">{f.code}</span>
                      <span className="text-red-400 font-mono">+{f.weight}</span>
                    </div>
                    <div className="text-white/40 text-[10px]">
                      {Object.entries(f.evidence || {}).map(([k, v]) => `${k}=${v}`).join(", ")}
                    </div>
                  </div>
                ))}
                {(c.flags || []).length === 0 && <div className="text-white/30 text-xs">None</div>}
              </div>
            </Card>
            <Card className="bg-[#0f0f0f] border-emerald-500/20 p-4" data-testid="trust-signals-panel">
              <div className="text-emerald-400 text-[10px] uppercase mb-2">Trust Signals</div>
              <div className="space-y-2">
                {(c.innocent_flags || []).map((f, i) => (
                  <div key={i} className="text-xs border-l-2 border-emerald-500/50 pl-2">
                    <div className="flex items-center justify-between">
                      <span className="text-white font-mono">{f.code}</span>
                      <span className="text-emerald-400 font-mono">{f.weight}</span>
                    </div>
                  </div>
                ))}
                {(c.innocent_flags || []).length === 0 && <div className="text-white/30 text-xs">None</div>}
              </div>
            </Card>
          </div>

          <Card className="bg-[#0f0f0f] border-white/5 p-4">
            <div className="text-white/40 text-[10px] uppercase mb-2">Related Commissions</div>
            <table className="w-full text-xs">
              <thead className="text-white/40 uppercase text-[9px]">
                <tr><th className="text-left">Payment</th><th className="text-left">Type</th><th className="text-right">Amount</th><th className="text-left">Status</th></tr>
              </thead>
              <tbody>
                {commissions.map((r, i) => (
                  <tr key={i} className="border-t border-white/[0.04]">
                    <td className="text-white/70 font-mono text-[10px]">{(r.payment_id || "").slice(0, 24)}</td>
                    <td className="text-white/60">{r.commission_type}</td>
                    <td className="text-right font-mono text-white/80">${((r.commission_amount || 0) / 100).toFixed(2)}</td>
                    <td><span className={`px-1.5 py-0.5 rounded text-[9px] uppercase ${r.status === "FROZEN" ? "bg-red-500/20 text-red-400" : "bg-white/5 text-white/60"}`}>{r.status}</span></td>
                  </tr>
                ))}
                {commissions.length === 0 && <tr><td colSpan="4" className="text-center text-white/30 py-3">—</td></tr>}
              </tbody>
            </table>
          </Card>

          <Card className="bg-[#0f0f0f] border-white/5 p-4" data-testid="case-notes-timeline-card">
            {(() => {
              const notes = c.admin_notes || [];
              // Timeline events excluding "add_note" — those are already
              // rendered as the top-level Case Note entries.
              const events = (c.timeline || []).filter((t) => {
                const label = t.event || t.action || "";
                return label !== "add_note";
              });
              // Merge into a single chronological list (newest first).
              const merged = [
                ...notes.map((n) => ({ __type: "note", at: n.at, data: n })),
                ...events.map((e) => ({ __type: "event", at: e.at, data: e })),
              ].sort((a, b) => (b.at || "").localeCompare(a.at || ""));

              return (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-white/40 text-[10px] uppercase">Case Notes + Timeline</div>
                    <span className="text-white/30 text-[10px]">{merged.length} total</span>
                  </div>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {merged.map((row, i) => {
                      if (row.__type === "note") {
                        const n = row.data;
                        return (
                          <div
                            key={`n-${i}`}
                            className="border-l-2 border-[#C5A55A]/50 pl-3 py-1"
                            data-testid={`case-note-${i}`}
                          >
                            <div className="flex items-center justify-between gap-3 mb-1">
                              <span className="text-[#C5A55A] font-medium text-xs">
                                {n.author_name || n.admin_id || "Unknown"}
                                {n.author_email && (
                                  <span className="text-white/40 font-normal ml-1">({n.author_email})</span>
                                )}
                              </span>
                              <span className="text-white/40 font-mono text-[10px] whitespace-nowrap">
                                {n.at ? new Date(n.at).toLocaleString(undefined, {
                                  year: "numeric", month: "short", day: "2-digit",
                                  hour: "2-digit", minute: "2-digit",
                                }) : "—"}
                              </span>
                            </div>
                            <div className="text-white/80 text-xs whitespace-pre-wrap break-words">{n.note}</div>
                          </div>
                        );
                      }
                      const e = row.data;
                      const label = e.event || e.action || "";
                      const actorId = e.actor_id || e.actor || "";
                      const actorName = e.actor_name || (actorId.startsWith("system") ? "System" : actorId || "Unknown");
                      const actorRole = e.actor_role || (actorId.startsWith("system") ? "system" : "user");
                      const detailNote = e.detail?.note || e.detail?.reason;
                      return (
                        <div
                          key={`e-${i}`}
                          className="border-l-2 border-white/10 pl-3 py-0.5 text-[11px]"
                          data-testid={`timeline-entry-${i}`}
                        >
                          <div className="text-white/60">
                            <span className="text-[#C5A55A]/80">{actorName}</span>
                            {actorId && (
                              <span className="text-white/40 ml-1">({actorRole}:{actorId})</span>
                            )}
                          </div>
                          <div className="text-white/60 font-mono">
                            <span className="text-white/40">{e.at?.slice(0, 19).replace("T", " ")}</span>
                            <span className="text-white/80 ml-2">{label}</span>
                          </div>
                          {detailNote && (
                            <div className="text-white/50 mt-0.5">{detailNote}</div>
                          )}
                        </div>
                      );
                    })}
                    {merged.length === 0 && (
                      <div className="text-white/30 text-xs italic">
                        No case activity yet — add a note on the right to start the history.
                      </div>
                    )}
                  </div>
                </>
              );
            })()}
          </Card>
        </div>

        <div className="space-y-2 lg:sticky lg:top-4 lg:h-fit">
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add note (optional)..."
            rows="3"
            className="w-full bg-black/40 border border-white/10 rounded p-2 text-xs text-white"
            data-testid="admin-note-input"
          />
          <Button
            size="sm"
            onClick={() => doAction("add_note")}
            disabled={busy || !note.trim()}
            variant="outline"
            className="w-full border-white/15 text-white/80 hover:bg-white/5 disabled:opacity-40"
            data-testid="action-save-note"
          >
            Save Note
          </Button>
          <label className="flex items-center gap-2 text-[11px] text-white/60 pt-1">
            <input type="checkbox" checked={cascade} onChange={(e) => setCascade(e.target.checked)} data-testid="cascade-checkbox" />
            Cascade-freeze downline
          </label>
          <Button size="sm" onClick={() => doAction("clear")} disabled={busy} className="w-full bg-emerald-500/80 hover:bg-emerald-500 text-black" data-testid="action-clear">Clear (Legit)</Button>
          {c.has_high_value && (
            <>
              <Button size="sm" onClick={approveHighValue} disabled={busy} className="w-full bg-cyan-500 hover:bg-cyan-400 text-black" data-testid="action-approve-hv">Approve High-Value</Button>
              <Button size="sm" onClick={splitHighValue} disabled={busy} variant="outline" className="w-full border-cyan-500/40 text-cyan-300" data-testid="action-split-hv">Split &lt;$500</Button>
            </>
          )}
          <Button size="sm" onClick={() => doAction("extend")} disabled={busy} variant="outline" className="w-full border-white/15 text-white/70" data-testid="action-extend">+72h Window</Button>
          <Button size="sm" onClick={() => doAction("escalate")} disabled={busy} className="w-full bg-amber-500 hover:bg-amber-400 text-black" data-testid="action-escalate">Escalate → Freeze</Button>
          <Button size="sm" onClick={() => doAction("request_info")} disabled={busy} variant="outline" className="w-full border-sky-500/40 text-sky-300" data-testid="action-request">Request Info</Button>
          <Button size="sm" onClick={() => doAction("downgrade_tier")} disabled={busy} variant="outline" className="w-full border-white/15 text-white/70" data-testid="action-downgrade">Downgrade Tier</Button>
          <Button size="sm" onClick={() => doAction("confirm_fraud")} disabled={busy} className="w-full bg-red-600 hover:bg-red-500 text-white" data-testid="action-confirm">Confirm Fraud</Button>
          <div className="pt-3 border-t border-white/5 mt-3">
            <Button size="sm" onClick={doVouch} disabled={busy} variant="outline" className="w-full border-sky-500/40 text-sky-300" data-testid="action-vouch">Vouch (12mo)</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
